import { CheckOutlined, InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { Button, Flex, Input, Select, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useGetAllAccounts } from '../../api/account.queries.ts';
import { useGetUserInfo } from '../../api/user-info.queries.ts';
import type { Account } from '../../generated/openapi/befunde-api';
import { AssignedUsersPopover } from './accounts/AssignedUsersPopover.tsx';
import { CreateAccountModal } from './accounts/CreateAccountModal.tsx';
import { UpdateAccountModal } from './accounts/UpdateAccountModal.tsx';

type StateFilter = 'all' | 'active' | 'inactive';

const useAccountsClassNames = () => {
  const { token } = theme.useToken();
  return {
    pageWrapper: css`
      padding: ${token.paddingLG}px;
    `,
  };
};

export const Accounts: React.FC = () => {
  const { token } = theme.useToken();
  const styles = useAccountsClassNames();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');

  const { data, isLoading, isFetching, refetch } = useGetAllAccounts();
  const { refetch: refetchUserInfo } = useGetUserInfo();

  const filteredAccounts: Account[] = useMemo(() => {
    let result =
      data?.items.filter(account => {
        if (debouncedSearch?.length) {
          const searchValue = debouncedSearch.toLowerCase();
          return (
            account.name.toLowerCase().includes(searchValue) ||
            account.authz
              .map(it => it.labId.toLowerCase())
              .join(' ')
              .includes(searchValue) ||
            account.authz
              .flatMap(it => it.identifiers)
              .map(it => it.toLowerCase())
              .join(' ')
              .includes(searchValue)
          );
        }
        return true;
      }) ?? [];

    if (stateFilter !== 'all') {
      result = result.filter(doctor => (stateFilter === 'active' ? !doctor.disabled : doctor.disabled));
    }

    return result;
  }, [data, debouncedSearch, stateFilter]);

  const columns: ColumnsType<Account> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      defaultSortOrder: 'ascend',
      width: 130,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Aktiv',
      dataIndex: 'disabled',
      key: 'disabled',
      width: 70,
      sorter: (a, b) => Number(b.disabled) - Number(a.disabled),
      render: value => (!value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Labors',
      dataIndex: 'authz',
      key: 'authz',
      width: 200,
      render: (_, record) => (
        <Flex wrap gap="small">
          {record.authz.map(a => (
            <Tag key={a.labId} style={{ margin: 0 }}>
              <Markable tokens={debouncedSearch ?? ''}>
                <span className={monoFont}>
                  {a.labId}
                  {a.identifiers.length > 0 && `: ${a.identifiers.join(', ')}`}
                </span>
              </Markable>
            </Tag>
          ))}
        </Flex>
      ),
    },
    {
      title: 'Theme',
      dataIndex: 'themeName',
      key: 'themeName',
      width: 120,
      ellipsis: true,
      sorter: (a, b) => (a.themeName ?? '').localeCompare(b.themeName ?? ''),
      render: value => value ?? <Typography.Text type="secondary">–</Typography.Text>,
    },
    {
      title: 'Benutzer',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 90,
      sorter: (a, b) => a.userCount - b.userCount,
      render: (value: number, record) => <AssignedUsersPopover accountId={record.id} count={value} />,
    },
  ];

  return (
    <div className={styles.pageWrapper}>
      <PageHeader
        title="Accounts"
        extra={
          <Flex wrap gap={token.paddingMD} align="center">
            <Select
              style={{ width: '130px' }}
              onChange={setStateFilter}
              value={stateFilter}
              options={[
                {
                  label: 'Alle',
                  value: 'all',
                },
                {
                  label: 'Nur Aktive',
                  value: 'active',
                },
                {
                  label: 'Nur Inaktive',
                  value: 'inactive',
                },
              ]}
            />
            <Input
              autoFocus
              allowClear
              placeholder="Suche"
              value={search ?? ''}
              onChange={e => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title="Suche nach Name, Laborkennung und Identifikator">
                  <InfoCircleOutlined />
                </Tooltip>
              }
              style={{ width: '250px' }}
            />
            <Button icon={<PlusCircleOutlined />} type="primary" onClick={() => setCreateModalOpen(true)}>
              Neuer Account
            </Button>
          </Flex>
        }
        style={{ padding: 0, paddingBottom: 'inherit' }}
      />
      <Table<Account>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        sticky={true}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredAccounts}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Accounts`,
        }}
        onRow={record => ({ onClick: () => setEditAccount(record), style: { cursor: 'pointer' } })}
        loading={isLoading || isFetching}
        columns={columns}
      />
      <CreateAccountModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          refetch();
        }}
      />
      <UpdateAccountModal
        account={editAccount}
        onClose={() => {
          setEditAccount(null);
          refetchUserInfo();
          refetch();
        }}
      />
    </div>
  );
};
